import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { brandInfo } from '../../data/products';

const policies = {
  privacy: {
    title: 'Privacy Policy',
    content: `
      <h3>Introduction</h3>
      <p>At Bright Vista, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy outlines how we collect, use, and safeguard your data when you visit our website or make a purchase.</p>

      <h3>Information We Collect</h3>
      <p>We collect information you provide directly to us, including:</p>
      <ul>
        <li>Name and contact information (email address, phone number, shipping address)</li>
        <li>Payment information (processed securely through our payment providers)</li>
        <li>Order history and preferences</li>
        <li>Communications you send to us</li>
      </ul>

      <h3>How We Use Your Information</h3>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Process and fulfill your orders</li>
        <li>Communicate with you about your orders and our products</li>
        <li>Send promotional communications (with your consent)</li>
        <li>Improve our website and services</li>
        <li>Comply with legal obligations</li>
      </ul>

      <h3>Information Sharing</h3>
      <p>We do not sell, trade, or rent your personal information to third parties. We may share your information with trusted service providers who assist us in operating our website and conducting our business, as long as they agree to keep this information confidential.</p>

      <h3>Data Security</h3>
      <p>We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. All payment transactions are encrypted using SSL technology.</p>

      <h3>Your Rights</h3>
      <p>You have the right to access, correct, or delete your personal information. You may also opt out of receiving marketing communications at any time by contacting us or using the unsubscribe link in our emails.</p>

      <h3>Contact Us</h3>
      <p>If you have any questions about this Privacy Policy, please contact us at ${brandInfo.email} or call ${brandInfo.phone}.</p>

      <p><em>Last updated: January 2026</em></p>
    `
  },
  terms: {
    title: 'Terms & Conditions',
    content: `
      <h3>Agreement to Terms</h3>
      <p>By accessing and using the Bright Vista website, you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, please do not use our website.</p>

      <h3>Products and Pricing</h3>
      <ul>
        <li>All prices are listed in US Dollars (USD) and are subject to change without notice.</li>
        <li>We reserve the right to modify or discontinue any product without prior notice.</li>
        <li>Product images are for illustration purposes and may vary slightly from actual products.</li>
        <li>We make every effort to display accurate colors, but we cannot guarantee that your device's display accurately reflects actual product colors.</li>
      </ul>

      <h3>Orders and Payment</h3>
      <ul>
        <li>All orders are subject to acceptance and availability.</li>
        <li>We reserve the right to refuse any order for any reason.</li>
        <li>Payment must be received in full before order processing begins.</li>
        <li>Cash on Delivery (COD) orders may require verification before shipment.</li>
      </ul>

      <h3>Intellectual Property</h3>
      <p>All content on this website, including text, graphics, logos, images, and software, is the property of Bright Vista and is protected by copyright and trademark laws. You may not reproduce, distribute, or create derivative works without our written permission.</p>

      <h3>Limitation of Liability</h3>
      <p>Bright Vista shall not be liable for any indirect, incidental, special, or consequential damages arising out of or in connection with the use of our website or products. Our total liability shall not exceed the amount paid for the product in question.</p>

      <h3>Governing Law</h3>
      <p>These Terms and Conditions shall be governed by and construed in accordance with the laws of the State of New York, without regard to its conflict of law provisions.</p>

      <h3>Changes to Terms</h3>
      <p>We reserve the right to update these Terms and Conditions at any time. Changes will be effective immediately upon posting to the website.</p>

      <h3>Contact</h3>
      <p>For questions about these Terms and Conditions, please contact us at ${brandInfo.email}.</p>

      <p><em>Last updated: January 2026</em></p>
    `
  },
  returns: {
    title: 'Refund & Return Policy',
    content: `
      <h3>30-Day Return Policy</h3>
      <p>At Bright Vista, we want you to be completely satisfied with your purchase. We offer a 30-day return policy on most items, starting from the date of delivery.</p>

      <h3>Eligibility for Returns</h3>
      <p>To be eligible for a return, your item must be:</p>
      <ul>
        <li>Unused and in the same condition as received</li>
        <li>In original packaging with all tags and labels attached</li>
        <li>Free from any damage, stains, or alterations</li>
        <li>Returned within 30 days of delivery</li>
      </ul>

      <h3>Non-Returnable Items</h3>
      <ul>
        <li>Custom or made-to-order furniture</li>
        <li>Clearance or final sale items</li>
        <li>Items damaged due to misuse or improper care</li>
        <li>Items without original packaging</li>
      </ul>

      <h3>Return Process</h3>
      <ol>
        <li>Contact our customer service team at ${brandInfo.email} or ${brandInfo.phone} to initiate a return.</li>
        <li>You will receive a Return Merchandise Authorization (RMA) number and instructions.</li>
        <li>Pack the item securely in its original packaging.</li>
        <li>Ship the item to our returns center using a trackable shipping method.</li>
      </ol>

      <h3>Refund Process</h3>
      <ul>
        <li>Refunds will be processed within 5-7 business days after we receive and inspect the returned item.</li>
        <li>Refunds will be issued to the original payment method.</li>
        <li>Shipping costs are non-refundable unless the return is due to our error.</li>
        <li>A restocking fee of 15% may apply to certain large furniture items.</li>
      </ul>

      <h3>Damaged or Defective Items</h3>
      <p>If you receive a damaged or defective item, please contact us within 48 hours of delivery with photos of the damage. We will arrange for a replacement or full refund at no additional cost to you.</p>

      <h3>Exchanges</h3>
      <p>We currently do not offer direct exchanges. Please return the original item for a refund and place a new order for the desired item.</p>

      <h3>Contact Us</h3>
      <p>For any questions about returns or refunds, please contact our customer service team at ${brandInfo.email} or ${brandInfo.phone}.</p>

      <p><em>Last updated: January 2026</em></p>
    `
  },
  shipping: {
    title: 'Shipping Policy',
    content: `
      <h3>Shipping Within the United States</h3>
      <p>Bright Vista ships to all 50 states within the United States. We partner with premium freight carriers to ensure your furniture arrives safely and in perfect condition.</p>

      <h3>Shipping Options</h3>
      <table>
        <tr>
          <th>Shipping Method</th>
          <th>Estimated Delivery</th>
          <th>Cost</th>
        </tr>
        <tr>
          <td>Standard Shipping</td>
          <td>7-14 business days</td>
          <td>Calculated at checkout</td>
        </tr>
        <tr>
          <td>Express Shipping</td>
          <td>3-5 business days</td>
          <td>Calculated at checkout</td>
        </tr>
        <tr>
          <td>White Glove Delivery</td>
          <td>10-21 business days</td>
          <td>Calculated at checkout</td>
        </tr>
      </table>

      <h3>Free Shipping</h3>
      <p>Enjoy FREE standard shipping on all orders over $999! Free shipping applies to the contiguous 48 states only.</p>

      <h3>White Glove Delivery</h3>
      <p>For larger furniture pieces, we offer premium White Glove delivery service, which includes:</p>
      <ul>
        <li>In-home delivery to the room of your choice</li>
        <li>Unpacking and assembly (if required)</li>
        <li>Removal of all packaging materials</li>
        <li>Inspection with you before sign-off</li>
      </ul>

      <h3>Order Processing</h3>
      <ul>
        <li>Orders are processed within 1-2 business days.</li>
        <li>You will receive a shipping confirmation email with tracking information once your order ships.</li>
        <li>Custom and made-to-order items may require additional processing time (4-8 weeks).</li>
      </ul>

      <h3>Delivery Information</h3>
      <ul>
        <li>A signature may be required upon delivery.</li>
        <li>Please inspect all items upon delivery and note any damage on the delivery receipt.</li>
        <li>We recommend having someone available to receive the delivery.</li>
      </ul>

      <h3>Shipping Restrictions</h3>
      <p>We currently only ship within the United States. We do not ship to P.O. Boxes, APO/FPO addresses, or international destinations.</p>

      <h3>Contact Us</h3>
      <p>For shipping inquiries, please contact us at ${brandInfo.email} or ${brandInfo.phone}.</p>

      <p><em>Last updated: January 2026</em></p>
    `
  }
};

export const PolicyModal = ({ isOpen, onClose, policyType }) => {
  const policy = policies[policyType];

  if (!policy) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] p-0 bg-[#FAF8F5] border-[#E8E4DE]" data-testid={`policy-modal-${policyType}`}>
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="font-['Cormorant_Garamond'] text-2xl md:text-3xl text-[#2D4A3E]">
            {policy.title}
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[60vh] px-6 pb-6">
          <div 
            className="policy-content font-['Montserrat'] text-[#5A6B5D] leading-relaxed text-sm"
            dangerouslySetInnerHTML={{ __html: policy.content }}
          />
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
